/* eslint-disable @typescript-eslint/no-unused-vars */
import { Company } from 'module/api/company/types';
import { useTranslation } from 'module/hooks/use-translation';
import { SidebarMenu as Menu } from 'module/ui/components/atoms/dropdown/menu';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Loading, PageLoading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import { FilterBar } from 'module/ui/components/molecules/filter-bar';
import {
  Short01,
  SingleLineEllipsis,
  HelperText01,
} from 'module/ui/components/styles';
import { Layout } from 'module/ui/components/templates/argon/components';
import {
  faChevronDown,
  faClockRotateLeft,
  faEdit,
  faEllipsisVertical,
  faArrowUpFromBracket,
  faFileLines,
  faFolder,
  faGlobe,
  faLoader,
  faPage,
  faSidebar,
  faSparkles,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useParams } from '@rocketlane/rocket-router';
import {
  Button,
  Button as RocketlaneButton,
  EmptyState as RocketlaneEmptyState,
  Icon,
  Input,
} from '@rocketlane/ui';
import { Editor as TiptapEditorType } from '@tiptap/core';
import { Tooltip } from 'antd';
import { DocumentationAgent } from 'api/document-agent/types';
import { PageType } from 'api/page-template/types';
import { PagesAPI } from 'api/pages';
import moment from 'moment';
import { queryClient } from 'queries';
import { useGetDocumentationAgentQuery } from 'queries/document-agent';
import {
  CompanyWiseSourceEmail,
  useCompanyV2SourcesWithScrollPagination,
} from 'queries/meeting';
import { useGetDocumentVersionHistoryQuery } from 'queries/pages';
import { useDeletePage } from 'queries/pages/use-delete-page';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import type { Option } from 'module/ui/components/atoms/select';
import { EmptyState } from 'components/atoms/empty-state';
import { MetadataTooltip } from 'components/common/metadata-tooltip';
import { prefetchImagesForExport } from 'components/organisms/document-editor/extensions/export-pdf/export-pdf-config';
import { useEditorExtensionsConfigForCompanyPage } from './editor-config';
import { useAccountSpacesRouteNavigations } from './route-navigations';
import { useCompanyPagesList } from './use-company-pages-list';
import { useCurrentSpaceDocument } from './use-current-space-document';
import { AgentSelectionScreen } from '../agent-selection-screen';
import { ChatProvider, useChatContext } from '../chat/chat-context';
import { ChatSidebarContent } from '../chat/chat-sidebar';
import { useChatTrigger } from '../chat/use-chat-trigger';
import { Document, type DocumentScrollHandle } from '../document/document';
import {
  VersionHistoryContent,
  VersionHistoryGroup,
} from '../document/version-history-sidebar';
import { DocumentAgentGenerationScreen } from '../document-agent-generation-screen';
import { AddSourceModal } from '../meeting-pages/add-source-modal';
import { MeetingPagesSidePane } from '../meeting-pages/side-pane';
import type { PageDocumentMappingMetadataStatus } from 'api/pages/types';
import type { NitroPagesPermissions } from 'types/nitro-pages';

const CHAT_PANE_KEY = 'chat';

type PageMappingMetadataLike =
  | {
      processed?: boolean;
      updating?: boolean;
      status?: PageDocumentMappingMetadataStatus;
    }
  | undefined;

function getPageMappingMetadataFlags(metadata: PageMappingMetadataLike) {
  const status = metadata?.status;
  const isErrored =
    status === 'GENERATION_ERRORED' || status === 'UPDATE_ERRORED';
  const isProcessing =
    status === 'GENERATING' ||
    status === 'UPDATING' ||
    (status == null &&
      (metadata?.processed === false || metadata?.updating === true));

  return { isProcessing, isErrored };
}

const LoadingWrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const Sidebar = styled(Layout.Sider)`
  flex: 0 0 294px !important;
  max-width: 294px !important;
  min-width: 294px !important;
  width: 294px !important;
  height: 100% !important;
  overflow: hidden !important;
  overflow-y: auto !important;
  background: var(--white-100);
  padding: var(--spacing-05);
  border-right: 1px solid var(--field-border-default);
  ${SearchInputExpanded} {
    margin-bottom: var(--spacing-05);
  }
`;
const SidebarInPopover = styled(Sidebar)`
  height: 70vh !important;
`;

/** Same role as meeting pages `Wrapper`: clips flex overflow so the side pane cannot expand the row. */
const AccountSpacesRoot = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
`;

const Title = styled.div`
  ${Short01};
  color: var(--text-primary);
  ${SingleLineEllipsis};
`;

const SecondaryFilter = styled(FilterBar.Secondary)`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const RightSidebar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-left: 1px solid var(--field-border-default);
  overflow: hidden;
  padding: var(--spacing-03);
  gap: var(--spacing-05);
`;
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  ${FilterBar} {
    flex-shrink: 0;
    box-shadow: none;
  }
`;
/** Matches meeting pages `PageContent` intent: main column fills remaining row width and can shrink. */
const PageContent = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  height: 100%;
  width: 100%;
`;
const SidebarMenu = styled(Menu)`
  min-width: 0;
  overflow-x: hidden;
  .ant-menu-item {
    height: 48px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: var(--spacing-02);
  padding: var(--spacing-02);
  background-color: white;
  border-radius: var(--border-radius-small);
  margin-bottom: var(--spacing-04);
  border: 2px solid var(--button-ghost-border);
  box-sizing: border-box;
  width: 100%;
`;

const Tab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  border: none;
  border-radius: var(--border-radius-small);
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--scarlet-gray-60)' : 'transparent'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--text-selected)' : 'var(--text-secondary)'};
  font-size: var(--font-size-01);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: var(--spacing-02);
`;

const SourcesContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-05);
`;

const SourcesHeader = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin-bottom: var(--spacing-04);
`;

const SourcesList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  position: relative;
`;

const SourceItem = styled.div`
  padding: var(--spacing-03);
  border: 1px solid var(--field-border-default);
  border-radius: var(--border-radius-small);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SourceItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
  flex: 1;
  min-width: 0;
`;

const SourceItemTitle = styled.div`
  ${Short01};
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SourceItemDate = styled.div`
  ${HelperText01};
  color: var(--text-tertiary);
`;

const RocketlaneEmptyStateRoot = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: var(--spacing-09) var(--spacing-08);
`;

/** Mirrors `DocumentAgentGenerationScreen` layout (no progress bar). */
const DocumentMappingGenerationErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  align-items: center;
  overflow-y: auto;
`;

const DocumentMappingGenerationErrorContainer = styled.div`
  padding: 48px 64px;
  height: 100%;
  background: #fff;
  min-width: 768px;
`;

const DocumentMappingGenerationErrorTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 40px;
  justify-content: space-between;
  width: 100%;
`;

const DocumentMappingGenerationErrorTitleInput = styled(Input)`
  font-size: 28px;
  line-height: 1.3;
  border: none;
  padding: 0 !important;
  width: 100%;

  input {
    font-size: 28px;
    border: none;
    padding: 0 !important;

    &:focus {
      outline: none;
      border: none;
    }

    &:disabled {
      color: #000;
    }
  }
`;

/** Same shell as `DocumentAgentGenerationScreen` `ProgressContainer`. */
const DocumentMappingGenerationErrorPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-large);
  padding: 16px;
  width: 652px;
  box-shadow: var(--shadow-08);
`;

const DocumentMappingGenerationErrorMessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--error-color);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

const DocumentMappingGenerationError = ({
  documentTitle,
  status,
}: {
  documentTitle: string;
  status?: PageDocumentMappingMetadataStatus;
}) => {
  const generationFailedLabel = useTranslation(
    'document_agent_generation.failed',
  );
  const updationFailedLabel = useTranslation(
    'document_agent_generation.updation_failed',
  );
  const failedLabel =
    status === 'UPDATE_ERRORED' ? updationFailedLabel : generationFailedLabel;

  return (
    <DocumentMappingGenerationErrorWrapper>
      <DocumentMappingGenerationErrorContainer>
        <DocumentMappingGenerationErrorTitleRow>
          <DocumentMappingGenerationErrorTitleInput
            value={documentTitle}
            disabled={true}
          />
        </DocumentMappingGenerationErrorTitleRow>
        <DocumentMappingGenerationErrorPanel>
          <DocumentMappingGenerationErrorMessageRow>
            <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
            <span>{failedLabel}</span>
          </DocumentMappingGenerationErrorMessageRow>
        </DocumentMappingGenerationErrorPanel>
      </DocumentMappingGenerationErrorContainer>
    </DocumentMappingGenerationErrorWrapper>
  );
};

interface AccountSpacesProps {
  company: Company;
  projectId?: number;
  isModuleFed?: boolean;
  /** When false, show empty state instead of agent selection in creation mode (user cannot invoke documentation agents). */
  documentAgentInvoke?: boolean;
  nitroPermissions?: NitroPagesPermissions;
}
const AccountSpacesContent = ({
  company,
  projectId,
  isModuleFed,
  documentAgentInvoke = true,
  nitroPermissions,
}: AccountSpacesProps) => {
  const { value, onChange, clear } = useSearchValueState();
  const { companyPages, isCompanyPagesLoading, allCompanyPages } =
    useCompanyPagesList({
      company,
      searchText: value,
    });

  const { spaceDocumentId, nitroPageId } = useParams<{
    spaceDocumentId?: string;
    nitroPageId?: string;
  }>();
  const isCreationMode = spaceDocumentId === 'new' || nitroPageId === 'new';

  const { spaceDocument, isSpaceDocumentLoading, refetchSpaceDocument } =
    useCurrentSpaceDocument({
      company,
      companyPages,
      isCompanyPagesLoading,
      isCreationMode,
      projectId,
      isModuleFed,
    });

  // Find the corresponding company page to get the sourceId
  const currentCompanyPage = companyPages.find(
    (page) => page.documentId === spaceDocument?.metadata.documentId,
  );
  const {
    navigateToSpaces,
    navigateToSpaceDocument,
    navigateToSpaceDocumentEdit,
    navigateToSpaceDocumentNew,
  } = useAccountSpacesRouteNavigations(projectId, isModuleFed);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sidePaneActiveKey, setSidePaneActiveKey] = useState<
    string | undefined
  >();
  const [activeSourceTab, setActiveSourceTab] = useState<'MEETING' | 'EMAIL'>(
    'MEETING',
  );
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);
  const { isChatOpen, setIsChatOpen, chatData, setChatData } = useChatContext();

  const [activeVersionNodeId, setActiveVersionNodeId] = useState<
    number | undefined
  >(undefined);
  const [activeVersionNodeType, setActiveVersionNodeType] = useState<
    string | undefined
  >(undefined);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const documentScrollContainerRef = useRef<DocumentScrollHandle>(null);
  const [editor, setEditor] = useState<TiptapEditorType | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const isEuRegion = useMemo(() => {
    try {
      const accountMetaStr = localStorage.getItem('accountMeta');
      const accountMeta = accountMetaStr ? JSON.parse(accountMetaStr) : null;

      return accountMeta?.clientRegion === 'eu';
    } catch {
      return false;
    }
  }, []);
  const documentImageUrlsRef = useRef<string[]>([]);
  const handleImageUrlsChange = useCallback((urls: string[]) => {
    documentImageUrlsRef.current = urls;
  }, []);
  const onEditorReady = useCallback((e: TiptapEditorType) => setEditor(e), []);

  const handleExportPdf = useCallback(async () => {
    if (!editor || isExportingPdf) {
      return;
    }
    setIsExportingPdf(true);
    try {
      const { convertToken } = await PagesAPI.getConvertToken();

      await prefetchImagesForExport(documentImageUrlsRef.current, editor);

      editor
        .chain()
        .exportPdf({
          token: convertToken,
        })
        .run();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PDF export failed:', error);
    } finally {
      setTimeout(() => setIsExportingPdf(false), 3000);
    }
  }, [editor, isExportingPdf]);

  const { data: versionHistoryData, isLoading: isVersionHistoryLoading } =
    useGetDocumentVersionHistoryQuery(
      spaceDocument?.metadata?.documentId ?? 0,
      { enabled: Boolean(spaceDocument) && !isCreationMode },
    );

  const groupedVersionHistory = useMemo<VersionHistoryGroup[]>(() => {
    if (!versionHistoryData || !Array.isArray(versionHistoryData)) {
      return [];
    }

    const grouped = new Map<string, VersionHistoryGroup>();

    versionHistoryData.forEach((item: any) => {
      if (!item.nodeId || !item.nodeType || !item.versions) {
        return;
      }

      const key = `${item.nodeType}-${item.nodeId}`;
      const existing = grouped.get(key);

      if (existing) {
        const existingVersionIds = new Set(existing.versions.map((v) => v.id));

        item.versions.forEach((version: any) => {
          if (!existingVersionIds.has(version.id)) {
            existing.versions.push(version);
          }
        });
      } else {
        let sectionTitle: string | undefined;
        let sectionType: string | undefined;
        let sectionId: number | undefined;

        if (item.versions && item.versions.length > 0) {
          const firstVersion = item.versions[0];

          if (firstVersion.content?.attrs?.title) {
            sectionTitle = firstVersion.content.attrs.title;
          }
          sectionId = firstVersion.content?.attrs?.templateSectionId ?? 0;

          sectionType =
            item.sectionType ?? firstVersion.content?.attrs?.sectionType;
        }

        grouped.set(key, {
          nodeId: item.nodeId,
          nodeType: item.nodeType,
          versions: item.versions || [],
          sectionTitle,
          sectionType,
          sectionId,
        });
      }
    });

    return Array.from(grouped.values());
  }, [versionHistoryData]);

  // Resolve page identifiers — prefer the list-level page entry; fall back
  // to the document metadata (e.g. when the page is filtered out by search).
  const deletePageMappingId =
    currentCompanyPage?.pageDocumentMappingId ??
    spaceDocument?.metadata.documentSourceMappingModel?.pageDocumentMappingId;
  const deleteDocumentId =
    currentCompanyPage?.documentId ?? spaceDocument?.metadata.documentId;
  const deletePageType =
    spaceDocument?.metadata.documentSourceMappingModel?.pageType;

  const { handleDelete } = useDeletePage({
    pageDocumentMappingId: deletePageMappingId,
    documentId: deleteDocumentId,
    pageType: deletePageType,
    companyId: projectId ?? company.companyId,
    allPages: allCompanyPages,
    navigateToPage: navigateToSpaceDocument,
    navigateToList: navigateToSpaces,
  });
  const isManualPage = currentCompanyPage?.pageType === PageType.Manual;
  const documentId = spaceDocument?.metadata.documentId || 0;
  const shouldFetchSources = !isManualPage && documentId > 0;

  const documentAgentId =
    spaceDocument?.metadata?.documentSourceMappingModel?.metadata
      ?.documentAgentId;

  const { data: documentAgent, isLoading: isDocumentAgentLoading } =
    useGetDocumentationAgentQuery(documentAgentId ?? 0, {
      enabled: !!documentAgentId && documentAgentId > 0,
    });

  const pageTypeIncludes = (pageType: string, keyword: string) =>
    pageType?.toLowerCase().includes(keyword.toLowerCase());

  const showMeetingDropdown = useMemo(() => {
    const sources = documentAgent?.configuration?.sources ?? [];

    return sources.some((s) => pageTypeIncludes(s.pageType, 'meeting'));
  }, [documentAgent?.configuration?.sources]);

  const showEmailDropdown = useMemo(() => {
    const sources = documentAgent?.configuration?.sources ?? [];

    return sources.some((s) => pageTypeIncludes(s.pageType, 'email'));
  }, [documentAgent?.configuration?.sources]);

  // Fetch meeting sources with infinite scroll
  const {
    sources: meetingSources,
    isLoading: isMeetingSourcesLoading,
    isFetchingNextPage: isFetchingMoreMeetings,
    hasNextPage: hasMoreMeetings,
    fetchNextPage: fetchMoreMeetings,
    refetch: refetchMeetingSources,
  } = useCompanyV2SourcesWithScrollPagination({
    documentId: shouldFetchSources ? documentId : 0,
    companyId: company.companyId,
    sourceType: 'MEETING',
    limit: 20,
    fetchUsed: true,
    isEnabled: true,
  });

  // Fetch email sources with infinite scroll
  const {
    sources: emailSources,
    isLoading: isEmailSourcesLoading,
    isFetchingNextPage: isFetchingMoreEmails,
    hasNextPage: hasMoreEmails,
    fetchNextPage: fetchMoreEmails,
    refetch: refetchEmailSources,
  } = useCompanyV2SourcesWithScrollPagination<CompanyWiseSourceEmail>({
    documentId: shouldFetchSources ? documentId : 0,
    companyId: company.companyId,
    sourceType: 'EMAIL',
    limit: 10,
    fetchUsed: true,
    isEnabled: true,
  });

  const refetchSpaceDocumentAndSources = useCallback(async () => {
    await refetchSpaceDocument();
    if (shouldFetchSources) {
      await Promise.all([refetchMeetingSources(), refetchEmailSources()]);
    }
  }, [
    refetchSpaceDocument,
    refetchMeetingSources,
    refetchEmailSources,
    shouldFetchSources,
  ]);

  // When there are no company pages, redirect to /new
  useEffect(() => {
    if (
      !isCompanyPagesLoading &&
      companyPages.length === 0 &&
      !isCreationMode
    ) {
      navigateToSpaceDocumentNew(projectId ?? company.companyId);
    }
  }, [
    isCompanyPagesLoading,
    companyPages.length,
    isCreationMode,
    navigateToSpaceDocumentNew,
    projectId,
    company.companyId,
  ]);

  // Close side pane and chat when switching between pages (documentId or route changes)
  useEffect(() => {
    setSidePaneActiveKey(undefined);
    setIsChatOpen(false);
  }, [documentId, setIsChatOpen]);

  useChatTrigger(); // Register the global chat trigger

  const openRightSideBar = useCallback((key: string) => {
    setSidePaneActiveKey(key);
    setIsSidebarOpen(false);
  }, []);

  // When chat is opened (e.g. from document), open right pane to chat and close left sidebar
  useEffect(() => {
    if (isChatOpen) {
      openRightSideBar(CHAT_PANE_KEY);
    }
  }, [isChatOpen, openRightSideBar]);

  const handleVersionHistoryClick = useCallback(
    (nodeId: number, nodeType: string) => {
      setActiveVersionNodeId(nodeId);
      setActiveVersionNodeType(nodeType);
      setSidePaneActiveKey('version-history');
      openRightSideBar('version-history');
      // Notify version-history triggers to recompute position after sidebar layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('version-history-pane-opened'));
        });
      });
    },
    [openRightSideBar],
  );

  const handleScrollToVersionSection = useCallback((sectionId: number) => {
    documentScrollContainerRef.current?.scrollToSection(sectionId);
  }, []);

  const handleCloseRightSidebar = useCallback(() => {
    if (sidePaneActiveKey === CHAT_PANE_KEY) {
      setIsChatOpen(false);
      window.dispatchEvent(new CustomEvent('closeInspector'));
    }
    setSidePaneActiveKey(undefined);
  }, [sidePaneActiveKey, setIsChatOpen]);

  const handleChatDataUsed = useCallback(() => {
    if (chatData) {
      setChatData({
        ...chatData,
        elementContext: '', // Reset only elementContext
      });
    }
  }, [setChatData, chatData]);

  const handleOpenCreationScreen = useCallback(() => {
    const companyIdForNavigation = projectId ?? company.companyId;

    navigateToSpaceDocumentNew(companyIdForNavigation);
  }, [navigateToSpaceDocumentNew, company.companyId, projectId]);

  const sidebarItems = useMemo(() => {
    return companyPages.length > 0
      ? [
          {
            key: 'account-spaces',
            icon: <FontAwesomeIcon icon={faFolder} size={12} />,
            label: 'General',
            children: companyPages.map((page) => ({
              label: <Title>{page.title}</Title>,
              key: page.documentId.toString(),
              icon:
                page.pageType !== PageType.Manual &&
                getPageMappingMetadataFlags(page.metadata).isProcessing ? (
                  <FontAwesomeIcon icon={faLoader} size={12} spin />
                ) : (
                  <FontAwesomeIcon icon={faPage} size={12} />
                ),
            })),
          },
        ]
      : [];
  }, [companyPages]);
  const searchPlaceholder = useTranslation('search');
  const noMeetingsFound = useTranslation('accounts.meetings.no_meetings_found');
  const noEmailsFound = useTranslation('accounts.email.no_email_found');
  const noInvokePermissionTitle = useTranslation(
    'accounts.nitro_pages.no_invoke_permission',
  );
  const noInvokePermissionDescription = useTranslation(
    'accounts.nitro_pages.no_invoke_permission_description',
  );
  const SideBar = useMemo(() => {
    return (
      <>
        <div className="flex flex-row gap-2">
          <SearchInputExpanded
            placeholder={searchPlaceholder}
            value={value}
            onChange={onChange}
            onClear={clear}
          />
          {(!isModuleFed ||
            (nitroPermissions &&
              nitroPermissions.canWriteCompanyPages &&
              documentAgentInvoke)) && (
            <RocketlaneButton
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
              onClick={handleOpenCreationScreen}
            />
          )}
        </div>

        <div className="flex flex-col">
          <SidebarMenu
            items={sidebarItems}
            onSelect={({ key }) => {
              const companyIdForNavigation = projectId ?? company.companyId;

              navigateToSpaceDocument(companyIdForNavigation, Number(key));
            }}
            selectedKeys={
              isCreationMode
                ? []
                : [spaceDocument?.metadata?.documentId.toString() ?? '']
            }
            defaultOpenKeys={['account-spaces']}
            expandIcon={<FontAwesomeIcon icon={faChevronDown} size={12} />}
            mode="inline"
          />
        </div>
      </>
    );
  }, [
    searchPlaceholder,
    value,
    onChange,
    clear,
    sidebarItems,
    spaceDocument?.metadata?.documentId,
    navigateToSpaceDocument,
    company.companyId,
    projectId,
    handleOpenCreationScreen,
    isCreationMode,
    isModuleFed,
    nitroPermissions,
    documentAgentInvoke,
  ]);
  const baseEditorExtensionsConfig = useEditorExtensionsConfigForCompanyPage();

  const editorExtensionsConfig = useMemo(() => {
    const config = { ...baseEditorExtensionsConfig };

    if (groupedVersionHistory.length > 0) {
      config.versionHistoryConfig = {
        enabled: true,
        versionHistory: groupedVersionHistory,
        onVersionHistoryClick: handleVersionHistoryClick,
      };
    }

    return config;
  }, [
    baseEditorExtensionsConfig,
    groupedVersionHistory,
    handleVersionHistoryClick,
  ]);

  const sidePaneItems = useMemo(() => {
    const mappingMetadata =
      spaceDocument?.metadata.documentSourceMappingModel?.metadata;
    const { isProcessing: isMappingProcessing } =
      getPageMappingMetadataFlags(mappingMetadata);
    const isPageEmpty = !spaceDocument || companyPages.length === 0;
    const shouldHideSources =
      isPageEmpty || isMappingProcessing || isManualPage;

    const chatItem = {
      key: CHAT_PANE_KEY,
      title: spaceDocument?.metadata.title ?? 'Chat',
      icon: <Icon icon={faSparkles} size="1x" />,
      disableManualOpen: true,
      content: (
        <div className="chat-sidebar flex min-h-0 flex-1 flex-col overflow-hidden">
          <ChatSidebarContent
            accountId={company.companyId}
            userId={303}
            chatData={chatData}
            onChatDataUsed={handleChatDataUsed}
          />
        </div>
      ),
    };

    if (shouldHideSources) {
      return [chatItem];
    }

    const showDocumentAgentLoader =
      !!documentAgentId && documentAgentId > 0 && isDocumentAgentLoading;

    const items: Array<{
      key: string;
      title: React.ReactNode;
      disableManualOpen?: boolean;
      icon: React.ReactNode;
      content: React.ReactNode;
    }> = [];

    // First tab: Sources (only for agent-generated documents)
    if (!shouldHideSources) {
      items.push({
        key: 'sources',
        title: <FormattedMessage id="sources" />,
        icon: <FontAwesomeIcon icon={faGlobe} size={12} />,
        content: (
          <SourcesContent>
            <SourcesHeader>
              <Button
                variant="primary"
                size="small"
                leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
                onClick={() => setIsAddSourceModalOpen(true)}
              >
                <FormattedMessage id="add" />
              </Button>
            </SourcesHeader>
            {showDocumentAgentLoader ? (
              <LoadingWrapper>
                <Loading size="small" />
              </LoadingWrapper>
            ) : (
              <>
                <TabContainer>
                  <Tab
                    $isActive={activeSourceTab === 'MEETING'}
                    onClick={() => setActiveSourceTab('MEETING')}
                  >
                    <FormattedMessage id="meetings" />
                  </Tab>
                  <Tab
                    $isActive={activeSourceTab === 'EMAIL'}
                    onClick={() => setActiveSourceTab('EMAIL')}
                  >
                    <FormattedMessage id="email" />
                  </Tab>
                </TabContainer>
                <SourcesList
                  onScroll={(e) => {
                    const target = e.currentTarget;
                    const scrollBottom =
                      target.scrollHeight -
                      target.scrollTop -
                      target.clientHeight;

                    // Load more when scrolled within 100px of bottom
                    if (scrollBottom < 100) {
                      if (
                        activeSourceTab === 'MEETING' &&
                        hasMoreMeetings &&
                        !isFetchingMoreMeetings
                      ) {
                        fetchMoreMeetings();
                      } else if (
                        activeSourceTab === 'EMAIL' &&
                        hasMoreEmails &&
                        !isFetchingMoreEmails
                      ) {
                        fetchMoreEmails();
                      }
                    }
                  }}
                >
                  {activeSourceTab === 'MEETING' ? (
                    isMeetingSourcesLoading && meetingSources.length === 0 ? (
                      <LoadingWrapper>
                        <Loading size="small" />
                      </LoadingWrapper>
                    ) : meetingSources.length > 0 ? (
                      <>
                        {meetingSources.map((meeting: any) => (
                          <SourceItem key={meeting.meetingId}>
                            <SourceItemContent>
                              <SourceItemTitle>
                                {meeting.meetingName}
                              </SourceItemTitle>
                              <SourceItemDate>
                                {moment(meeting.startTime).format(
                                  'DD MMM YYYY',
                                )}
                              </SourceItemDate>
                            </SourceItemContent>
                            {/* <Button
                              variant="ghost"
                              leftIcon={
                                <FontAwesomeIcon
                                  icon={faEllipsisVertical}
                                  size={12}
                                />
                              }
                            /> */}
                          </SourceItem>
                        ))}
                        {isFetchingMoreMeetings && (
                          <LoadingWrapper>
                            <Loading size="small" />
                          </LoadingWrapper>
                        )}
                      </>
                    ) : (
                      <EmptyState
                        title={noMeetingsFound}
                        size="small"
                        centerVertically
                      />
                    )
                  ) : isEmailSourcesLoading && emailSources.length === 0 ? (
                    <LoadingWrapper>
                      <Loading size="small" />
                    </LoadingWrapper>
                  ) : emailSources.length > 0 ? (
                    <>
                      {emailSources.map((email: any) => (
                        <SourceItem key={email.emailThreadId}>
                          <SourceItemContent>
                            <SourceItemTitle>{email.subject}</SourceItemTitle>
                          </SourceItemContent>
                          {/* <Button
                            variant="ghost"
                            leftIcon={
                              <FontAwesomeIcon
                                icon={faEllipsisVertical}
                                size={12}
                              />
                            }
                          /> */}
                        </SourceItem>
                      ))}
                      {isFetchingMoreEmails && (
                        <LoadingWrapper>
                          <Loading size="small" />
                        </LoadingWrapper>
                      )}
                    </>
                  ) : (
                    <EmptyState
                      title={noEmailsFound}
                      size="small"
                      centerVertically
                    />
                  )}
                </SourcesList>
              </>
            )}
          </SourcesContent>
        ),
      });
    }

    // Second tab: Version history (if any section has versions)
    if (groupedVersionHistory.length > 0) {
      items.push({
        key: 'version-history',
        title: 'Answer History',
        disableManualOpen: false,
        icon: <FontAwesomeIcon icon={faClockRotateLeft} size={12} />,
        content: (
          <VersionHistoryContent
            versionHistory={groupedVersionHistory}
            activeNodeId={activeVersionNodeId}
            activeNodeType={activeVersionNodeType}
            onScrollToSection={handleScrollToVersionSection}
          />
        ),
      });
    }

    return [...items, chatItem];
  }, [
    spaceDocument,
    companyPages.length,
    isManualPage,
    documentAgentId,
    isDocumentAgentLoading,
    activeSourceTab,
    meetingSources,
    noMeetingsFound,
    noEmailsFound,
    emailSources,
    isMeetingSourcesLoading,
    isEmailSourcesLoading,
    isFetchingMoreMeetings,
    isFetchingMoreEmails,
    hasMoreMeetings,
    hasMoreEmails,
    fetchMoreMeetings,
    fetchMoreEmails,
    groupedVersionHistory,
    activeVersionNodeId,
    activeVersionNodeType,
    handleScrollToVersionSection,
    company.companyId,
    chatData,
    handleChatDataUsed,
  ]);

  if (isCompanyPagesLoading) {
    return <PageLoading />;
  }

  // Extract mapping metadata for cleaner conditionals
  const mappingMetadata =
    spaceDocument?.metadata.documentSourceMappingModel?.metadata;
  const {
    isProcessing: isDocumentProcessing,
    isErrored: isDocumentMappingErrored,
  } = getPageMappingMetadataFlags(mappingMetadata);

  return (
    <AccountSpacesRoot>
      <PageWrapper>
        {isSidebarOpen && <Sidebar>{SideBar}</Sidebar>}

        <PageContent>
          <MainContent ref={mainContentRef}>
            <FilterBar>
              <FilterBar.Primary>
                {isCompanyPagesLoading ||
                allCompanyPages.length === 0 ? null : (
                  <Popover
                    content={
                      isSidebarOpen ? null : (
                        <SidebarInPopover>{SideBar}</SidebarInPopover>
                      )
                    }
                    placement="bottomLeft"
                    destroyTooltipOnHide
                    isDisabled={isSidebarOpen}
                    mouseEnterDelay={0.3}
                  >
                    <Button
                      variant="ghost"
                      leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
                      onClick={() => {
                        if (sidePaneActiveKey == null) {
                          setIsSidebarOpen(!isSidebarOpen);
                        }
                      }}
                    />
                  </Popover>
                )}
              </FilterBar.Primary>
              <SecondaryFilter>
                {spaceDocument != null &&
                  !isSpaceDocumentLoading &&
                  !isCreationMode &&
                  !isDocumentProcessing &&
                  !isDocumentMappingErrored &&
                  (!isModuleFed ||
                    (nitroPermissions &&
                      nitroPermissions.canReadCompanyPages)) && (
                    <>
                      <MetadataTooltip
                        updatedAt={spaceDocument.metadata.updatedAt}
                        createdAt={spaceDocument.metadata.createdAt}
                      />

                      {!isEuRegion && (
                        <Tooltip
                          title={
                            isExportingPdf ? 'Exporting...' : 'Export as PDF'
                          }
                          placement="bottom"
                        >
                          <span>
                            <Button
                              variant="ghost"
                              size="medium"
                              leftIcon={
                                <FontAwesomeIcon
                                  icon={faArrowUpFromBracket}
                                  size={16}
                                />
                              }
                              onClick={handleExportPdf}
                              disabled={!editor || isExportingPdf}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {(!isModuleFed ||
                        (nitroPermissions &&
                          nitroPermissions.canWriteCompanyPages)) && (
                        <Button
                          variant="primary"
                          leftIcon={<FontAwesomeIcon icon={faEdit} size={12} />}
                          onClick={() => {
                            const companyIdForNavigation =
                              projectId ?? company.companyId;

                            navigateToSpaceDocumentEdit(
                              companyIdForNavigation,
                              spaceDocument.metadata.documentId,
                            );
                          }}
                        >
                          <FormattedMessage id="edit" />
                        </Button>
                      )}
                      {(!isModuleFed ||
                        (nitroPermissions &&
                          nitroPermissions.canDeleteCompanyPages)) && (
                        <MoreActionsDropdown
                          menu={{
                            onClick: ({ key }) => {
                              if (key === 'delete') {
                                handleDelete();
                              }
                            },
                            items: [
                              {
                                type: 'group',
                                label: <FormattedMessage id="more_actions" />,
                                children: [
                                  {
                                    key: 'delete',
                                    label: <FormattedMessage id="delete" />,
                                    icon: <FontAwesomeIcon icon={faTrash} />,
                                    danger: true,
                                    disabled: !deletePageMappingId,
                                  },
                                ],
                              },
                            ],
                          }}
                          triggerElement={
                            <Button
                              variant="ghost"
                              leftIcon={
                                <FontAwesomeIcon
                                  icon={faEllipsisVertical}
                                  size={12}
                                />
                              }
                            />
                          }
                        />
                      )}
                    </>
                  )}
              </SecondaryFilter>
            </FilterBar>

            {isCreationMode ? (
              documentAgentInvoke ? (
                <AgentSelectionScreen
                  companyId={company.companyId}
                  projectId={projectId}
                  isModuleFed={isModuleFed}
                />
              ) : (
                <RocketlaneEmptyStateRoot>
                  <RocketlaneEmptyState
                    icon={{ icon: faFileLines }}
                    message={noInvokePermissionTitle}
                    description={noInvokePermissionDescription}
                  />
                </RocketlaneEmptyStateRoot>
              )
            ) : isSpaceDocumentLoading ||
              (spaceDocument?.metadata?.documentId &&
                isVersionHistoryLoading) ? (
              <LoadingWrapper>
                <Loading size="large" />
              </LoadingWrapper>
            ) : spaceDocument == null ? (
              <></>
            ) : isDocumentMappingErrored ? (
              <DocumentMappingGenerationError
                documentTitle={spaceDocument.metadata.title}
                status={mappingMetadata?.status}
              />
            ) : isDocumentProcessing ? (
              <DocumentAgentGenerationScreen
                companyId={company.companyId}
                documentTitle={spaceDocument.metadata.title}
                documentId={spaceDocument.metadata.documentId}
                updating={mappingMetadata?.updating === true}
                onComplete={() => {
                  void refetchSpaceDocumentAndSources();
                }}
              />
            ) : (
              <PageWrapper
                style={{ flex: 1, minHeight: 0, minWidth: 0 }}
                className="document-content-wrapper"
              >
                <Document
                  isChatOpen={isChatOpen}
                  document={spaceDocument}
                  readonly={true}
                  pageType={PageType.Company}
                  sourceId={currentCompanyPage?.sourceId}
                  editorExtensionsConfig={editorExtensionsConfig}
                  scrollContainerRef={documentScrollContainerRef}
                  onEditorReady={onEditorReady}
                  onImageUrlsChange={handleImageUrlsChange}
                />
              </PageWrapper>
            )}
          </MainContent>
        </PageContent>

        {!isCreationMode &&
          !isDocumentProcessing &&
          !isDocumentMappingErrored &&
          !isManualPage && (
            <MeetingPagesSidePane
              items={sidePaneItems}
              activeKey={sidePaneActiveKey}
              onSelect={(key) => {
                if (key === sidePaneActiveKey) {
                  handleCloseRightSidebar();
                } else {
                  if (sidePaneActiveKey === CHAT_PANE_KEY) {
                    setIsChatOpen(false);
                    window.dispatchEvent(new CustomEvent('closeInspector'));
                  }
                  if (key === 'version-history') {
                    setActiveVersionNodeId(undefined);
                    setActiveVersionNodeType(undefined);
                  }
                  openRightSideBar(key);
                }
              }}
              onClose={() => {
                handleCloseRightSidebar();
                setActiveVersionNodeId(undefined);
                setActiveVersionNodeType(undefined);
              }}
            />
          )}

        <AddSourceModal
          isOpen={isAddSourceModalOpen}
          onClose={() => setIsAddSourceModalOpen(false)}
          documentId={spaceDocument?.metadata.documentId}
          companyId={company.companyId}
          onSuccess={() => void refetchSpaceDocumentAndSources()}
          showMeetingDropdown={showMeetingDropdown}
          showEmailDropdown={showEmailDropdown}
        />
      </PageWrapper>
    </AccountSpacesRoot>
  );
};

export const AccountSpaces = ({
  company,
  projectId,
  isModuleFed,
  documentAgentInvoke,
  nitroPermissions,
}: AccountSpacesProps) => {
  return (
    <ChatProvider>
      <AccountSpacesContent
        company={company}
        projectId={projectId}
        isModuleFed={isModuleFed}
        documentAgentInvoke={documentAgentInvoke}
        nitroPermissions={nitroPermissions}
      />
    </ChatProvider>
  );
};
